import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DateHelper } from '@/common/helpers/date-helper';

dayjs.extend(utc);
dayjs.extend(timezone);

type GuideUser = {
  id: string;
  customId: string;
  language: string;
  browser: string;
  device: string;
  os: string;
  customParams: Record<string, any>;
  firstInteraction?: string;
  createdAt?: string;
  /** Interação ANTERIOR a esta visita, devolvida pelo identify — não "agora". */
  lastInteraction?: string;
  updatedAt?: string;
};

export const filterBySegmentation = <T>(
  items: (T & {
    segments?: Array<{ id: string; rules: any }>;
    segmentsCondition?: 'AND' | 'OR';
    forceStart?: boolean;
    userId?: string;
  })[],
  guideUser: GuideUser | null,
  timezone: string
): (T & {
  segments?: Array<{ id: string; rules: any }>;
  segmentsCondition?: 'AND' | 'OR';
  forceStart?: boolean;
})[] => {
  if (!guideUser) {
    return items;
  }

  return items.filter(item => {
    if (!item.segments || item.segments.length === 0) {
      return true;
    }

    if (guideUser.id !== item.userId) {
      return false;
    }

    const segmentResults = item.segments.map(segment => {
      if (!segment || !segment.rules) {
        return false;
      }

      return evaluateSegmentationRules(segment.rules, guideUser, timezone);
    });

    const segmentsCondition = item.segmentsCondition || 'AND';

    if (segmentsCondition === 'AND') {
      return segmentResults.every((result: boolean) => result === true);
    } else {
      return segmentResults.some((result: boolean) => result === true);
    }
  });
};

export const evaluateSegmentationRules = (
  segmentationRules: any,
  guideUser: GuideUser,
  timezone: string
): boolean => {
  if (!segmentationRules.filters || segmentationRules.filters.length === 0) {
    return true;
  }

  const filterResults = segmentationRules.filters.map((filter: any) => {
    if (!filter.conditions || filter.conditions.length === 0) {
      return true;
    }

    return filter.conditions.some((condition: any) => {
      return evaluateCondition(condition, guideUser, timezone);
    });
  });

  return filterResults.every((result: boolean) => result === true);
};

export const evaluateCondition = (
  condition: any,
  guideUser: GuideUser,
  timezone: string
): boolean => {
  const { field, operator, value } = condition;
  const userValue = getUserFieldValue(field, guideUser);

  switch (operator) {
    case 'IS':
      return safeToLowerCase(userValue) === safeToLowerCase(value);
    case 'IS_NOT':
      return safeToLowerCase(userValue) !== safeToLowerCase(value);
    case 'CONTAINS':
      return (
        typeof userValue === 'string' && safeToLowerCase(userValue).includes(safeToLowerCase(value))
      );
    case 'NOT_CONTAINS':
      return (
        typeof userValue === 'string' &&
        !safeToLowerCase(userValue).includes(safeToLowerCase(value))
      );
    case 'STARTS_WITH':
      return (
        typeof userValue === 'string' &&
        safeToLowerCase(userValue).startsWith(safeToLowerCase(value))
      );
    case 'ENDS_WITH':
      return (
        typeof userValue === 'string' && safeToLowerCase(userValue).endsWith(safeToLowerCase(value))
      );
    case 'IS_NOT_KNOWN':
      return userValue === null || userValue === undefined || userValue === '';
    case 'HAS_ANY_VALUE':
      return userValue !== null && userValue !== undefined && userValue !== '';

    case 'GREATER_THAN':
      return typeof userValue === 'number' && userValue > Number(value);
    case 'LESS_THAN':
      return typeof userValue === 'number' && userValue < Number(value);
    case 'GREATER_THAN_OR_EQUAL':
      return typeof userValue === 'number' && userValue >= Number(value);
    case 'LESS_THAN_OR_EQUAL':
      return typeof userValue === 'number' && userValue <= Number(value);

    case 'IS_TRUE':
      return userValue === true;
    case 'IS_FALSE':
      return userValue === false;

    case 'RELATIVE_LESS_THAN':
    case 'RELATIVE_EXACTLY':
    case 'RELATIVE_MORE_THAN':
      return evaluateRelativeDateCondition(operator, value, userValue, timezone);

    case 'ABSOLUTE_BEFORE':
    case 'ABSOLUTE_ON':
    case 'ABSOLUTE_AFTER':
      return evaluateAbsoluteDateCondition(operator, value, userValue, timezone);

    default:
      return false;
  }
};

export const getUserFieldValue = (field: string, guideUser: GuideUser): any => {
  switch (field) {
    case 'LANGUAGE':
      return guideUser.language;
    case 'BROWSER':
      return guideUser.browser;
    case 'DEVICE':
      return guideUser.device;
    case 'OS':
      return guideUser.os;
    case 'USER_ID':
      return guideUser.customId;
    case 'FIRST_INTERACTION':
      return guideUser.firstInteraction || guideUser.createdAt;
    case 'LAST_INTERACTION':
      return guideUser.lastInteraction || guideUser.updatedAt;
    default:
      if (field.startsWith('CUSTOM_')) {
        const customFieldName = field.replace('CUSTOM_', '');
        return guideUser.customParams?.[customFieldName];
      }
      return null;
  }
};

const safeToLowerCase = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).toLowerCase();
};

const parseRelativeDateValue = (value: string): { amount: number; unit: string } | null => {
  const parts = value.split('_');
  if (parts.length !== 2) return null;

  const amount = parseInt(parts[0]);
  const unit = parts[1];

  if (isNaN(amount) || !['days', 'weeks', 'months'].includes(unit)) {
    return null;
  }

  return { amount, unit };
};

const calculateReferenceDate = (amount: number, unit: string): dayjs.Dayjs => {
  const now = dayjs();

  switch (unit) {
    case 'days':
      return now.subtract(amount, 'day');
    case 'weeks':
      return now.subtract(amount, 'week');
    case 'months':
      return now.subtract(amount, 'month');
    default:
      return now;
  }
};

const evaluateRelativeDateCondition = (
  operator: string,
  value: string,
  userDateValue: any,
  timezone: string
): boolean => {
  if (!userDateValue) return false;

  const parsed = parseRelativeDateValue(value);
  if (!parsed) return false;

  const referenceDate = calculateReferenceDate(parsed.amount, parsed.unit);
  const userDate = DateHelper.parseDateWithTimezone(userDateValue, timezone);
  if (!userDate) return false;

  switch (operator) {
    case 'RELATIVE_LESS_THAN':
      return userDate.isAfter(referenceDate);
    case 'RELATIVE_EXACTLY':
      return userDate.isSame(referenceDate, 'day');
    case 'RELATIVE_MORE_THAN':
      return userDate.isBefore(referenceDate);
    default:
      return false;
  }
};

const evaluateAbsoluteDateCondition = (
  operator: string,
  value: string,
  userDateValue: any,
  timezone: string
): boolean => {
  if (!userDateValue) return false;

  const dateValue = value.includes('|') ? value.split('|')[0] : value;
  const valueTimezone = value.includes('|') ? value.split('|')[1] : timezone;

  const targetDate = dayjs.tz(dateValue, valueTimezone);
  if (!targetDate.isValid()) return false;

  const userDate = DateHelper.parseDateWithTimezone(userDateValue, valueTimezone);
  if (!userDate) return false;

  switch (operator) {
    case 'ABSOLUTE_BEFORE':
      return userDate.isBefore(targetDate, 'day');
    case 'ABSOLUTE_ON':
      return userDate.isSame(targetDate, 'day');
    case 'ABSOLUTE_AFTER':
      return userDate.isAfter(targetDate, 'day');
    default:
      return false;
  }
};
